/**
 * @file Airport & city picker backed by `GET search-airport/v3`.
 *
 * v3 returns **ranked** results and groups the 23 multi-airport metros, so a
 * search for "new york" yields one New York (NYC) row containing JFK, LGA and
 * EWR. Both the metro row and each member airport are independently
 * selectable, the way Google Flights and Kayak behave.
 *
 * The object handed to `onChange` is a superset of the old v2 row — it still
 * carries `id`, `iata`, `name`, `city` and `country_name` alongside the
 * canonical v3 `resolved_airport_id`, `code`, `label` and `sub_label`. That is
 * why the eleven existing call sites needed no changes. New code should read the
 * v3 names; `resolved_airport_id` is the only durable handle, since no endpoint
 * converts an IATA code back into a database id.
 *
 * `onChange` keeps MUI's `(event, value)` signature for the same reason.
 */
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Combobox, Loader, Pill, PillsInput, TextInput, VisuallyHidden, useCombobox } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';

import { zIndex } from 'theme/zIndex';
import { MIN_QUERY_LENGTH, DEFAULT_LIMIT, flattenResults, formatSelection, selectionKey } from 'utils/airport-search';
import { tidySubLabel, highlightRuns, isCodeHit } from 'utils/airport-display';
import { useAirportSearch } from 'hooks/use-airport-search';

import css from './airportAutocomplete.module.css';

/** Emphasis by weight, never colour — see `highlightRuns` for why it may render nothing. */
const Marked = ({ text, query }) =>
  highlightRuns(text, query).map((run, index) => (run.hit ? <mark key={index}>{run.text}</mark> : <span key={index}>{run.text}</span>));

Marked.propTypes = {
  text: PropTypes.string,
  query: PropTypes.string
};

/**
 * Render-only flags added by `flattenResults`. Stripped before the object
 * reaches consumer state so nothing persists a row's transient UI status.
 */
const RENDER_ONLY_FIELDS = ['depth', 'role', 'selectable', 'selected', 'isLastChild', 'childCount'];

const toSelection = (row) => Object.fromEntries(Object.entries(row).filter(([key]) => !RENDER_ONLY_FIELDS.includes(key)));

// eslint-disable-next-line react/display-name
const AirportAutocomplete = forwardRef(
  (
    {
      error,
      helperText,
      searchMode,
      textFieldLabel,
      // Accepted for backward compatibility with the MUI-era call sites; Mantine
      // has no 'outlined'/'standard' variants, so this is deliberately unused.
      variant,
      minSearchInput = MIN_QUERY_LENGTH,
      /** Max *selections* in `multiple` mode — the pre-existing meaning. */
      limit = 3,
      /** Max *results* to request from the API (1–25). */
      resultLimit = DEFAULT_LIMIT,
      startIconAdornment = null,
      value,
      onChange,
      multiple = false,
      /**
       * When false a metro row renders as a non-selectable header and only its
       * member airports can be picked. Use it wherever a bare IATA string is
       * persisted: a metro code like `NYC` is not an airport code.
       */
      allowCities = true,
      size: sizeProp,
      sx,
      ...others
    },
    ref
  ) => {
    const size = sizeProp || 'md';
    const selections = useMemo(() => (multiple ? value || [] : []), [multiple, value]);
    const atLimit = multiple && selections.length >= limit;

    const [search, setSearch] = useState(() => (multiple ? '' : formatSelection(value)));

    const combobox = useCombobox({
      onDropdownClose: () => combobox.resetSelectedOption()
    });

    // While the field is just displaying a completed selection there is nothing
    // to search for — "New York (JFK)" would only return noise.
    const isShowingSelection = !multiple && Boolean(value) && search === formatSelection(value);

    const {
      results,
      error: searchError,
      isOffline,
      retry,
      resolvedQuery,
      isLoading,
      isRefining,
      isBelowMin,
      isEmpty
    } = useAirportSearch({
      query: search,
      limit: resultLimit,
      mode: searchMode,
      minLength: minSearchInput,
      enabled: !isShowingSelection && !atLimit
    });

    const selectedKeys = useMemo(() => new Set(selections.map(selectionKey)), [selections]);

    const rows = useMemo(() => flattenResults(results, { allowCities, selectedKeys }), [results, allowCities, selectedKeys]);

    const rowsByUid = useMemo(() => new Map(rows.map((row) => [row.uid, row])), [rows]);

    const currentKey = !multiple && value ? selectionKey(value) : null;

    /**
     * Identity of the value the field is already showing. Without it, the
     * `onChange(null, null)` fired when the user types over a completed
     * selection would round-trip through the parent and wipe what they just
     * typed — so both handlers below pre-record the value they are about to
     * push, and the effect ignores that echo.
     */
    const reflectedKey = useRef(selectionKey(value));

    // Take over the field only when the parent genuinely seeds or resets it.
    useEffect(() => {
      if (multiple) return;
      const key = selectionKey(value);
      if (key === reflectedKey.current) return;
      reflectedKey.current = key;
      setSearch(formatSelection(value));
    }, [multiple, value]);

    // v3 is ranked best-first, so Enter should take the top hit. Mantine never
    // auto-selects, which is why `jfk` + Enter does nothing today.
    useEffect(() => {
      if (combobox.dropdownOpened && rows.length > 0) combobox.selectFirstOption();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, combobox.dropdownOpened]);

    const removeSelection = useCallback(
      (item) => {
        const key = selectionKey(item);
        onChange?.(
          null,
          selections.filter((entry) => selectionKey(entry) !== key)
        );
      },
      [onChange, selections]
    );

    const handleOptionSubmit = (uid) => {
      const row = rowsByUid.get(uid);
      if (!row || !row.selectable) return;
      const picked = toSelection(row);

      if (!multiple) {
        setSearch(formatSelection(picked));
        reflectedKey.current = selectionKey(picked);
        onChange?.(null, picked);
        combobox.closeDropdown();
        return;
      }

      // Re-picking a chosen row removes it — the only in-list undo there is.
      if (selectedKeys.has(selectionKey(picked))) {
        removeSelection(picked);
        return;
      }
      if (selections.length >= limit) return;
      onChange?.(null, [...selections, picked]);
      setSearch('');
    };

    const handleInput = (next) => {
      setSearch(next);
      combobox.openDropdown();
      // Typing over a completed single selection clears it, so a wizard can
      // never advance on a stale airport the field is no longer showing.
      if (!multiple && value && next !== formatSelection(value)) {
        reflectedKey.current = '';
        onChange?.(null, null);
      }
    };

    const renderRow = (row) => {
      const isHeader = row.role === 'cityHeader';
      const isChild = row.depth === 1;
      const isCity = row.role === 'city' || isHeader;
      const sub = tidySubLabel(row);
      // Only when it actually differs — EWR reads "· Newark" under New York,
      // while JFK and LGA render nothing.
      const note = isChild && row.city && row.parent_city_name && row.city !== row.parent_city_name ? row.city : null;

      const ariaLabel = (() => {
        if (isHeader) {
          return `${row.label}, ${row.code}. Metro area with ${row.airport_count} airports. Choose an airport below.`;
        }
        if (isCity) {
          return `${row.label}, ${row.code}. All ${row.airport_count} airports. ${sub}`;
        }
        if (isChild) {
          return (
            `${row.label}, ${row.code}. In the ${row.parent_city_label || 'metro'} metro area` +
            `${note ? `, ${note}` : ''}.${row.selected ? ' Selected, press Enter to remove.' : ''}`
          );
        }
        return `${row.label}, ${row.code}. ${sub}${row.selected ? ' Selected, press Enter to remove.' : ''}`;
      })();

      return (
        <Combobox.Option
          key={row.uid}
          value={row.uid}
          className={css.option}
          data-role={isChild ? 'child' : row.role}
          data-has-children={row.childCount > 0 || undefined}
          data-last-child={row.isLastChild || undefined}
          // Never Mantine's `selected` prop: it writes the same
          // data-combobox-selected attribute the keyboard cursor uses, so it
          // cannot mean "already chosen".
          data-chosen={row.selected || undefined}
          // `disabled` is what makes Mantine's getNextIndex/getPreviousIndex skip
          // the row — the single reason a header is not a keyboard trap.
          disabled={isHeader || undefined}
          aria-disabled={isHeader || undefined}
          active={!multiple && currentKey === selectionKey(row)}
          aria-label={ariaLabel}
        >
          <span
            className={css.badge}
            data-variant={isCity ? 'metro' : 'airport'}
            data-hit={isCodeHit(row.code, resolvedQuery) || undefined}
          >
            {row.code}
          </span>

          <span className={css.text}>
            <span className={css.label}>
              <Marked text={row.label} query={resolvedQuery} />
            </span>
            {/* Children stay single-line: their sub_label is byte-identical to
                their siblings' and to the parent's. */}
            {!isChild && (
              <span className={css.sub}>
                <Marked text={sub} query={resolvedQuery} />
              </span>
            )}
          </span>

          {note && <span className={css.note}>{note}</span>}

          {isCity && row.childCount > 0 && (
            <span className={css.countChip}>{allowCities ? `All ${row.airport_count} airports` : `${row.airport_count} airports`}</span>
          )}

          {row.selected && <IconCheck className={css.check} size={16} stroke={2.5} aria-hidden />}
        </Combobox.Option>
      );
    };

    // Announce settled states only — never mid-debounce, or it chatters on
    // every keystroke.
    const announcement = useMemo(() => {
      if (searchError) return isOffline ? 'You are offline' : 'Airport search is unavailable';
      if (isLoading) return 'Searching airports';
      if (isEmpty) return `No airports match ${resolvedQuery}`;
      if (resolvedQuery && results.length) {
        return `${results.length} result${results.length === 1 ? '' : 's'} for ${resolvedQuery}`;
      }
      return '';
    }, [searchError, isOffline, isLoading, isEmpty, resolvedQuery, results.length]);

    const spinner = isLoading || isRefining ? <Loader size={size === 'sm' ? 14 : 16} /> : null;

    return (
      <>
        <Combobox
          store={combobox}
          onOptionSubmit={handleOptionSubmit}
          withinPortal
          keepMounted={false}
          position="bottom-start"
          offset={4}
          zIndex={zIndex.popover}
        >
          {multiple ? (
            <Combobox.DropdownTarget>
              <PillsInput
                label={textFieldLabel}
                size={size}
                error={error || undefined}
                description={helperText || undefined}
                classNames={{ input: css.input }}
                rightSection={spinner}
                onClick={() => combobox.openDropdown()}
                style={sx}
              >
                <Pill.Group>
                  {selections.map((item) => (
                    <Pill
                      key={selectionKey(item)}
                      withRemoveButton
                      classNames={{ label: css.pillLabel }}
                      onRemove={() => removeSelection(item)}
                      removeButtonProps={{ 'aria-label': `Remove ${formatSelection(item)}` }}
                    >
                      <span className={css.pillCode}>{item.code || item.iata}</span>
                      <span className={css.pillText}>{item.type === 'city' ? item.label : item.city || item.name}</span>
                    </Pill>
                  ))}

                  <Combobox.EventsTarget withAriaAttributes withExpandedAttribute>
                    {/* Combobox.EventsTarget injects aria-controls and aria-expanded via
                        cloneElement, which jsx-a11y cannot see statically. Setting them here
                        instead would not work — cloneElement's config wins over child props. */}
                    {/* eslint-disable-next-line jsx-a11y/role-has-required-aria-props */}
                    <PillsInput.Field
                      ref={ref}
                      role="combobox"
                      aria-autocomplete="list"
                      value={search}
                      placeholder={selections.length ? '' : 'Search airports'}
                      onChange={(event) => handleInput(event.currentTarget.value)}
                      onFocus={() => combobox.openDropdown()}
                      onBlur={() => combobox.closeDropdown()}
                      onKeyDown={(event) => {
                        // Standard and keyboard-essential; Mantine omits it.
                        if (event.key === 'Backspace' && search.length === 0 && selections.length > 0) {
                          event.preventDefault();
                          removeSelection(selections[selections.length - 1]);
                        }
                      }}
                    />
                  </Combobox.EventsTarget>
                </Pill.Group>
              </PillsInput>
            </Combobox.DropdownTarget>
          ) : (
            <Combobox.Target withAriaAttributes withExpandedAttribute>
              {/* eslint-disable-next-line jsx-a11y/role-has-required-aria-props */}
              <TextInput
                ref={ref}
                label={textFieldLabel}
                size={size}
                role="combobox"
                aria-autocomplete="list"
                classNames={{ input: css.input }}
                value={search}
                onChange={(event) => handleInput(event.currentTarget.value)}
                onFocus={() => combobox.openDropdown()}
                onClick={() => combobox.openDropdown()}
                onBlur={() => combobox.closeDropdown()}
                error={error || undefined}
                description={helperText || undefined}
                leftSection={startIconAdornment}
                rightSection={
                  spinner ||
                  (value ? (
                    <Combobox.ClearButton
                      aria-label="Clear airport"
                      onClear={() => {
                        setSearch('');
                        onChange?.(null, null);
                      }}
                    />
                  ) : null)
                }
                style={sx}
                {...others}
              />
            </Combobox.Target>
          )}

          <Combobox.Dropdown className={css.dropdown}>
            <div className={css.inner} data-size={size}>
              {multiple && (
                <div className={css.header}>
                  <span className={css.headerCount} data-at-limit={atLimit || undefined}>
                    {selections.length} of {limit} selected
                  </span>
                  {selections.length > 0 && (
                    <button
                      type="button"
                      className={css.headerAction}
                      // Without this the field blurs first and the click is lost.
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => onChange?.(null, [])}
                    >
                      Clear all
                    </button>
                  )}
                </div>
              )}

              {isRefining && <span className={css.refiningBar} aria-hidden />}

              {atLimit && (
                <div className={css.state}>
                  You&apos;ve selected the maximum of {limit} airports.
                  <span className={css.stateHint}>Remove one to add another.</span>
                </div>
              )}

              {/* Searching is suspended while the field reflects a completed pick, so
                  without this the dropdown would open completely empty. */}
              {isShowingSelection && (
                <div className={css.state}>
                  {formatSelection(value)} selected.
                  <span className={css.stateHint}>Type to search for a different airport.</span>
                </div>
              )}

              {!atLimit && !isShowingSelection && isBelowMin && (
                <div className={css.state}>Type at least {minSearchInput} characters to search.</div>
              )}

              {isLoading && (
                <div aria-hidden>
                  {[0, 1, 2].map((row) => (
                    <div key={row} className={css.skelRow}>
                      <span className={css.skelBadge} />
                      <span className={css.skelText}>
                        <span className={css.skelLine} data-line="label" />
                        <span className={css.skelLine} data-line="sub" />
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {searchError && (
                <div className={css.state} data-tone="error">
                  {isOffline ? "You're offline." : 'Airport search is unavailable.'}
                  <span className={css.stateHint}>{isOffline ? 'Check your connection and try again.' : 'Try again in a moment.'}</span>
                  <div className={css.stateAction}>
                    <Button size="xs" variant="default" onMouseDown={(event) => event.preventDefault()} onClick={retry}>
                      Try again
                    </Button>
                  </div>
                </div>
              )}

              {isEmpty && (
                <div className={css.state}>
                  No airports match &ldquo;{resolvedQuery}&rdquo;.
                  <span className={css.stateHint}>Try a city, an airport name, or a 3-letter code like JFK.</span>
                </div>
              )}

              {rows.length > 0 && (
                <Combobox.Options
                  className={css.options}
                  aria-label={`${textFieldLabel || 'Airport'} results`}
                  aria-busy={isRefining || undefined}
                >
                  {rows.map(renderRow)}
                </Combobox.Options>
              )}
            </div>
          </Combobox.Dropdown>
        </Combobox>

        {/* Outside the dropdown: with keepMounted={false} an unmounted live
            region never announces. */}
        <VisuallyHidden role="status" aria-live="polite">
          {announcement}
        </VisuallyHidden>
      </>
    );
  }
);

AirportAutocomplete.propTypes = {
  error: PropTypes.node,
  helperText: PropTypes.node,
  textFieldLabel: PropTypes.node,
  minSearchInput: PropTypes.number,
  variant: PropTypes.string,
  value: PropTypes.any,
  limit: PropTypes.number,
  resultLimit: PropTypes.number,
  multiple: PropTypes.bool,
  allowCities: PropTypes.bool,
  startIconAdornment: PropTypes.node,
  searchMode: PropTypes.string,
  onChange: PropTypes.func,
  size: PropTypes.string,
  sx: PropTypes.object
};

export default AirportAutocomplete;
